"use client";

/**
 * MIN-294 — `AgentChat.Settings`: the ai app's own settings context (model,
 * usage), separate from the chat core `Root` establishes. A descendant of
 * `Root`, never an ancestor — `Root` itself never reads it (see the issue's
 * "What to build" anatomy block). `ModelSelector` / `Context` read this
 * context through `useAgentChatSettings` and throw when it is absent: a
 * missing provider is a loud programming error, not a silently inert control
 * — the provider app never imports either, so its own chat context has no
 * model field to be null and no dead branch to maintain.
 *
 * `PromptInput`'s submit path reads the optional variant
 * (`useAgentChatSettingsOptional`) instead: it adapts when a `Settings`
 * provider is present (folding the chosen model into the next thread's
 * creation body) without requiring one — the provider app's shape composes
 * `Root` + `Thread` + `PromptInput` with no `Settings` anywhere above them at
 * all.
 *
 * There is no system prompt here any more. The engine has one prompt author —
 * the role — and the `system_prompts` table it read from is gone.
 */
import { createContext, type ReactNode, use, useMemo } from "react";

export interface AgentChatSettingsContextValue {
  model: string;
  onModelChange: (model: string) => void;
  models: string[];
}

const AgentChatSettingsContext = createContext<AgentChatSettingsContextValue | null>(null);

export interface AgentChatSettingsProps extends AgentChatSettingsContextValue {
  children: ReactNode;
}

export function AgentChatSettings({ children, model, onModelChange, models }: AgentChatSettingsProps) {
  const value = useMemo<AgentChatSettingsContextValue>(
    () => ({ model, onModelChange, models }),
    [model, onModelChange, models],
  );
  return <AgentChatSettingsContext value={value}>{children}</AgentChatSettingsContext>;
}

/** Throws when rendered outside `<AgentChat.Settings>` — read by
 * `ModelSelector`/`Context`, which have nothing sensible to render
 * without it. */
export function useAgentChatSettings(): AgentChatSettingsContextValue {
  const ctx = use(AgentChatSettingsContext);
  if (!ctx) {
    throw new Error("AgentChat.ModelSelector/Context must be rendered inside <AgentChat.Settings>");
  }
  return ctx;
}

/** Reads the settings context without throwing — for parts that adapt when
 * settings are present without requiring them (`PromptInput`'s submit path;
 * the provider app's chat has none). */
export function useAgentChatSettingsOptional(): AgentChatSettingsContextValue | null {
  return use(AgentChatSettingsContext);
}
